import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { createAdEventId, trackClick, trackImpression } from '../../services/adService';

const Widget = styled.aside`
  position: fixed;
  z-index: 160;
  left: max(1rem, env(safe-area-inset-left));
  bottom: calc(env(safe-area-inset-bottom, 0px) + var(--cookie-banner-offset, 1rem));
  width: min(22rem, calc(100vw - 2rem));
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0.9rem;
  background: ${props => props.theme.colors.card};
  color: ${props => props.theme.colors.cardForeground};
  box-shadow: 0 12px 36px rgb(0 0 0 / 0.22);

  @media (max-width: 480px) {
    left: max(0.75rem, env(safe-area-inset-left));
    width: calc(100vw - 1.5rem);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const SponsoredLabel = styled.span`
  display: inline-block;
  margin-bottom: 0.4rem;
  color: ${props => props.theme.colors.mutedForeground};
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`;

const DismissButton = styled.button`
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 2rem;
  height: 2rem;
  margin: -0.4rem -0.4rem 0 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.theme.colors.mutedForeground};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.muted};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1rem;
  line-height: 1.35;
`;

const Message = styled.p`
  margin: 0.55rem 0 0;
  color: ${props => props.theme.colors.mutedForeground};
  font-size: 0.875rem;
  line-height: 1.45;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.85rem;
`;

const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 2.25rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primaryForeground || '#fff'};
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none;

  &:focus-visible {
    outline: 3px solid ${props => props.theme.colors.foreground};
    outline-offset: 2px;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const NavigationButton = styled.button`
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 50%;
  background: ${props => props.theme.colors.card};
  color: ${props => props.theme.colors.foreground};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const SlideCount = styled.span`
  min-width: 2.5rem;
  color: ${props => props.theme.colors.mutedForeground};
  font-size: 0.75rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const ROTATION_DELAY_MS = 5000;

export default function SponsoredNotificationWidget({
  ads,
  isVisible,
  language,
  pagePath,
  onDismiss,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewNumber, setViewNumber] = useState(0);
  const trackedViews = useRef(new Set());
  const ad = ads[activeIndex] || null;

  useEffect(() => {
    setActiveIndex(0);
    setViewNumber(value => value + 1);
  }, [ads]);

  useEffect(() => {
    if (!isVisible || ads.length < 2) return undefined;
    const timerId = window.setTimeout(() => {
      setActiveIndex(index => (index + 1) % ads.length);
      setViewNumber(value => value + 1);
    }, ROTATION_DELAY_MS);
    return () => window.clearTimeout(timerId);
  }, [activeIndex, ads.length, isVisible]);

  useEffect(() => {
    if (!isVisible || !ad?._id) return;
    const viewKey = `${ad._id}:${viewNumber}`;
    if (trackedViews.current.has(viewKey)) return;
    trackedViews.current.add(viewKey);
    trackImpression(ad._id, {
      eventId: createAdEventId(),
      language,
      pagePath,
    });
  }, [ad, isVisible, language, pagePath, viewNumber]);

  if (!isVisible || !ad) return null;

  const changeSlide = direction => {
    setActiveIndex(index => (index + direction + ads.length) % ads.length);
    setViewNumber(value => value + 1);
  };

  const trackAffiliateClick = () => {
    trackClick(ad._id, {
      eventId: createAdEventId(),
      language,
      pagePath,
    });
  };

  return (
    <Widget aria-label="Sponsored notification">
      <Header>
        <div>
          <SponsoredLabel>Sponsored</SponsoredLabel>
          {ad.title && <Title>{ad.title}</Title>}
        </div>
        <DismissButton
          type="button"
          onClick={() => onDismiss(ad)}
          aria-label="Dismiss sponsored notification"
        >
          <X size={18} aria-hidden="true" />
        </DismissButton>
      </Header>
      {ad.message && <Message aria-live="polite">{ad.message}</Message>}
      <Footer>
        <Cta
          href={ad.clickUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={trackAffiliateClick}
        >
          {ad.ctaText || 'Learn more'}
        </Cta>
        {ads.length > 1 && (
          <Controls aria-label="Sponsored message controls">
            <NavigationButton
              type="button"
              onClick={() => changeSlide(-1)}
              aria-label="Previous sponsored message"
            >
              <ChevronLeft size={17} aria-hidden="true" />
            </NavigationButton>
            <SlideCount>{activeIndex + 1} / {ads.length}</SlideCount>
            <NavigationButton
              type="button"
              onClick={() => changeSlide(1)}
              aria-label="Next sponsored message"
            >
              <ChevronRight size={17} aria-hidden="true" />
            </NavigationButton>
          </Controls>
        )}
      </Footer>
    </Widget>
  );
}
